import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { usePapaParse, useCSVDownloader } from 'react-papaparse';
import { orderBy, uniqBy } from 'lodash';
import styled from 'styled-components';
import { Checkbox, Col, Divider, Row, Upload } from 'antd5';
import { DownloadOutlined, UploadOutlined } from '@ant-design5/icons';
import { Button, Collapse, TooltipWithQuestionIcon, useMessage } from 'components/ui';

import { useAccountContext } from 'utils/account/AccountContext';
import { SKYCRAWLER_UPLOAD, AERO_ENTERPRISE_UPLOAD } from 'utils/release-toggles';
import { uploadIngestFiles, useGetIngestUploadUrls } from 'utils/ingest';
import {
  AUTONOMOUS_DRONE,
  MANUAL_DRONE,
  BLADE_INTERNAL,
  CSV_BULK_UPLOAD_OPTIONS,
  JSON_BULK_UPLOAD_OPTIONS,
} from 'utils/constants';
import { useUploadContext } from 'components/FileUploader';
import { getBulkUploadEventTypes } from '../../utils';
import {
  AtlasGqlDecimalSeparator,
  AtlasGqlFileFieldDelimiter,
  AtlasGqlFileFormat,
  AtlasGqlInspectionType,
  AtlasGqlMetadataFieldMapping,
  AtlasGqlValidateImageMetadataFileInput,
  useGetImageMetadataFieldsQuery,
  useValidateImageMetadataFileMutation,
} from 'types/atlas-graphql';
import {
  CloseOnSuccessProps,
  InspectionUploadWorkflowComponentProps,
  Steps,
  UploadTypes,
} from '../UploadInspectionDataModal';
import { ButtonContainer } from '../UploadInspectionDataModal.style';
import { FieldMappingForm } from '../FieldMappingForm';
import { EXAMPLE_IMAGE_DETAILS, ExampleImageDetails } from '../constants';
import { Preview } from './Preview';
import { ImageDetailsValidationProps, Validation } from './Validation';
import { SkyCrawlerUpload } from './SkyCrawlerUpload';
import { AeroEnterpriseUpload } from './AeroEnterpriseUpload';

type ImageDetailsProps = Omit<
  InspectionUploadWorkflowComponentProps,
  'setValidationStepProps' | 'setCreatedInspectionIds' | 'setCreatedTaskIds'
> & {
  initialStep?: Steps;
  inspectionType?: AtlasGqlInspectionType;
  inspectionIds: string[];
  uploadType: UploadTypes;
  onCloseModal: () => void;
  onProcessComplete: (input: CloseOnSuccessProps) => void;
  fileType: AtlasGqlFileFormat;
  setFileType: Dispatch<SetStateAction<AtlasGqlFileFormat>>;
  validateImageMetadataInputRef: MutableRefObject<
    AtlasGqlValidateImageMetadataFileInput | undefined
  >;
  externalState: ImageDetailsState;
};

export type ImageDetailsState = {
  file: File | undefined;
  setFile: Dispatch<SetStateAction<File | undefined>>;
  providedFieldNames: string[] | undefined;
  setProvidedFieldNames: Dispatch<SetStateAction<string[] | undefined>>;
  previewData:
    | {
        [key: string]: string | number;
      }[]
    | undefined;
  setPreviewData: Dispatch<
    SetStateAction<
      {
        [key: string]: string | number;
      }[]
    >
  >;
};

export const ImageDetails: React.FC<ImageDetailsProps> = ({
  requireTaskId,
  step,
  setStep,
  initialStep,
  setUploadErrors,
  inspectionIds,
  inspectionTasks,
  inspectionType,
  uploadType,
  uploadId,
  onCloseModal,
  onProcessComplete,
  onComplete,
  fileType,
  setFileType,
  validateImageMetadataInputRef,
  externalState,
}) => {
  const { hasReleaseToggle, features } = useAccountContext();
  const hasSkyCrawlerUploadRT = hasReleaseToggle(SKYCRAWLER_UPLOAD);
  const hasAeroEnterpriseUploadRT = hasReleaseToggle(AERO_ENTERPRISE_UPLOAD);
  const {
    SKYCRAWLER_UPLOAD: hasSkyCrawlerUploadFlag,
    AERO_ENTERPRISE_UPLOAD: hasAeroEnterpriseUploadFlag,
  } = features;

  const { file, setFile, previewData, setPreviewData } = externalState;

  const [providedFieldNames, setProvidedFieldNames] = useState<string[]>();
  const [fieldMappings, setFieldMappings] = useState<AtlasGqlMetadataFieldMapping[] | undefined>();
  const [shouldAutoRelease, setShouldAutoRelease] = useState<boolean>(true);
  const [inspectionUploadEventTypes, setInspectionUploadEventTypes] = useState(() =>
    getBulkUploadEventTypes(CSV_BULK_UPLOAD_OPTIONS.CSV_BULK)
  );
  const [likelyDelimiterError, setLikelyDelimiterError] = useState<boolean>(false);
  const [cellDelimiter, setCellDelimiter] = useState<AtlasGqlFileFieldDelimiter>(
    AtlasGqlFileFieldDelimiter.Comma
  );
  const [decimalDelimiter, setDecimalDelimiter] = useState<AtlasGqlDecimalSeparator>(
    AtlasGqlDecimalSeparator.Period
  );
  const [validationStepProps, setValidationStepProps] = useState<ImageDetailsValidationProps>();

  /**
   * TODO: SC-125542
   *   - state for validation step
   *   - call props.setUploadErrors(uploadErrors => ({...uploadErrors, imageDetails: TODO}))
   *     as appropriate if validation has errors
   */

  const { message } = useMessage();
  const { readString } = usePapaParse();

  const { data: imageMetadataFieldsData } = useGetImageMetadataFieldsQuery({
    variables: inspectionType?.id ? { inspectionTypeId: inspectionType.id } : undefined,
  });
  const imageMetadataFields = orderBy(
    imageMetadataFieldsData?.imageMetadataFields ?? [],
    [
      field => {
        const priority: { [key: string]: number } = { id: 1, taskId: 2 };
        return priority[field.internalName] ?? Infinity;
      },
      field => field.internalName,
    ],
    ['asc', 'asc']
  );

  const internalFields = useMemo(() => {
    const fields = uniqBy(imageMetadataFields, 'internalName');

    if (requireTaskId) {
      return fields.map(field =>
        field.internalName === 'taskId' ? { ...field, required: true } : field
      );
    }

    return fields;
  }, [imageMetadataFields, requireTaskId]);

  const existingTaskCount = inspectionTasks?.length ?? 0;

  const { exampleCsv, exampleJson } = useMemo(() => {
    if (internalFields.length === 0) {
      return { exampleCsv: '', exampleJson: '' };
    }

    let exampleImageDetails: ExampleImageDetails[] = EXAMPLE_IMAGE_DETAILS;

    if (inspectionTasks && existingTaskCount) {
      const existingTaskIds: string[] = inspectionTasks.map(({ taskId }) => taskId);

      exampleImageDetails = existingTaskIds.map((taskId, i) => ({
        id: '',
        taskId,
        path: `path/to/image${i}`,
        imageUrl: `https://example.com/image${i}`,
        blade: '',
        bladeSide: '',
        radialDistance: '',
        height: '',
        direction: '',
      }));
    }

    const exampleCsv =
      internalFields.map(field => field.defaultCsvHeader ?? field.internalName).join(',') +
      '\n' +
      exampleImageDetails
        .map(details =>
          internalFields.map(field => details[field.internalName as keyof typeof details]).join(',')
        )
        .join('\n');

    const exampleData = exampleImageDetails.map(details =>
      Object.fromEntries(
        internalFields.map(field => [
          field.defaultJsonFieldName ?? field.internalName,
          details[field.internalName as keyof typeof details],
        ])
      )
    );
    const exampleJson = `data:application/json,${JSON.stringify(exampleData)}`;

    return { exampleCsv, exampleJson };
  }, [internalFields, inspectionTasks, existingTaskCount]);

  const parseFile = useCallback(
    async (file: File) => {
      if (file.type === 'text/csv') {
        const csvText = await file.text();

        const {
          data,
          meta,
        }: {
          data: { [key: string]: string }[];
          meta: { delimiter: string; fields: string[]; [key: string]: any };
        } = readString(csvText, {
          header: true,
          skipEmptyLines: true,
          /**
           * Papaparse will auto-detect the delimiter by default, but passing it
           * explicitly so the user will be forced to select the cell delimiter
           * before firing the mutation (to ensure the correct delimiter is passed).
           */
          delimiter: {
            COMMA: ',',
            SEMICOLON: ';',
            TAB: '\t',
          }[cellDelimiter],
        });

        if (meta.fields.length !== Object.keys(data[0]).length) {
          message.error(
            'Mismatch in columns between header and data rows. Please check your file and reupload.'
          );
        } else {
          setProvidedFieldNames(meta.fields);
          setFileType(AtlasGqlFileFormat.Csv);
          setLikelyDelimiterError(meta.fields.length === 1);
          setPreviewData(data);
        }
      } else if (file.type === 'application/json') {
        const jsonText = await file.text();

        try {
          const parsedJson = JSON.parse(jsonText);
          /**
           * This assumes (for now) that all records are the same format and are
           * simple key/value pairs. Based on the requirements, this seems fine
           * for now (especially since we have an example file). We may want to support
           * more options in the future.
           */
          if (Array.isArray(parsedJson) && parsedJson.length) {
            setProvidedFieldNames(Object.keys(parsedJson[0]));
            setFileType(AtlasGqlFileFormat.Json);
            setPreviewData(parsedJson);
          }
        } catch (error) {
          message.error('Error parsing JSON. Please verify formatting and try again.');
        }
      } else {
        message.error('File type not supported. Please upload a .csv or .json file.');
      }
    },
    [cellDelimiter]
  );

  const handleUpload = ({ file }: { file: File; fileList: File[] }) => {
    setFile(file);
    parseFile(file);
  };

  useEffect(() => {
    if (file) {
      parseFile(file);
    }
  }, [cellDelimiter]);

  useEffect(() => {
    const inspectionType = {
      [AtlasGqlFileFormat.Csv]: CSV_BULK_UPLOAD_OPTIONS.CSV_BULK,
      [AtlasGqlFileFormat.Json]: JSON_BULK_UPLOAD_OPTIONS.JSON_BULK,
    }[fileType];

    setInspectionUploadEventTypes(() => getBulkUploadEventTypes(inspectionType));
  }, [fileType]);

  useEffect(() => {
    if (providedFieldNames?.length && step !== Steps.DetailsPreview) {
      setStep(Steps.DetailsMapping);
    }
  }, [providedFieldNames]);

  const { getIngestUploadUrls } = useGetIngestUploadUrls();
  const [validateImageMetadataFile] = useValidateImageMetadataFileMutation();
  const { setSelectedFiles, setUploading } = useUploadContext();

  const handleValidate = async (allImagesHaveUrls: boolean) => {
    if (file) {
      setUploading(true);

      const dataType = inspectionUploadEventTypes.inspectionType;
      const fileName = file.name;

      // Get signed url for upload
      try {
        const { items } = await getIngestUploadUrls({
          dataType,
          files: [{ fileName }],
          prefix: dataType,
          recordId: uploadId,
        });

        const [uploadItem] = items;
        if (!uploadItem) {
          message.error('Error uploading image details: Upload URL could not be obtained');
          setUploading(false);
          return;
        }

        setSelectedFiles([
          {
            name: fileName,
            path: '',
            percent: 0,
            sourceName: fileName,
            type: 'file',
            valid: true,
          },
        ]);

        // Upload file to S3
        await uploadIngestFiles({
          items: [{ ...uploadItem, file }],
        });

        // Once the file is in S3, call mutation to process
        const input: AtlasGqlValidateImageMetadataFileInput = {
          uploadId,
          ...(uploadType === UploadTypes.Single ? { inspectionId: inspectionIds[0] } : {}),
          key: uploadItem.fields.key,
          bucket: uploadItem.fields.bucket,
          fileFormat: fileType,
          decimalSeparator: decimalDelimiter,
          delimiter: cellDelimiter,
          fieldMappings,
        };

        // Set the input object in parent state to use for submitting the process mutation
        validateImageMetadataInputRef.current = input;

        // Validate the image metadata
        const { data } = await validateImageMetadataFile({
          variables: { input },
        });

        setUploading(false);
        setSelectedFiles([]);

        if (data?.validateImageMetadataFile?.failureCount) {
          setValidationStepProps({
            validateImageMetadataFile: data?.validateImageMetadataFile,
            fileName: file?.name ?? 'your details file',
            setStep,
            setUploadErrors,
          });
          setStep(Steps.DetailsValidation);
          setUploadErrors(uploadErrors => ({
            ...uploadErrors,
            imageDetails: true,
          }));
        } else {
          if (allImagesHaveUrls) {
            // We don't have to upload images, because they all have URLs.
            // So call the onProcessComplete callback with empty input instead
            onProcessComplete({});
          } else {
            setStep(Steps.ImageUpload);
          }
        }
      } catch (error) {
        message.error(`Error uploading image details: ${error.message}`);
        setUploading(false);
        return;
      }
    }
  };

  const { CSVDownloader } = useCSVDownloader();
  // Declaring a styled component inside component because CSVDownloader must be desctructured from useCSVDownloader
  const StyledCSVDownloader = styled(CSVDownloader)<{ marginRight?: string }>`
    background: transparent;
    border: 0;

    ${({ marginRight }) => marginRight && `margin-right: ${marginRight}`}
  `;

  const handleBack = () => {
    setStep(
      uploadType === UploadTypes.Multiple ? Steps.SummaryValidation : Steps.SingleUploadValidation
    );
  };

  const shouldShowSkyCrawler =
    hasSkyCrawlerUploadRT &&
    hasSkyCrawlerUploadFlag &&
    inspectionIds.length === 1 &&
    [BLADE_INTERNAL].includes(inspectionType?.name ?? '');
  const shouldShowAeroEnterprise =
    hasAeroEnterpriseUploadRT &&
    hasAeroEnterpriseUploadFlag &&
    inspectionIds.length === 1 &&
    [AUTONOMOUS_DRONE, MANUAL_DRONE].includes(inspectionType?.name ?? '');

  const defaultCreatePanel = (
    <div>
      {[Steps.DetailsUpload, Steps.DetailsMapping].includes(step) && (
        <>
          {existingTaskCount ? (
            <p>
              <strong>Upload details for {existingTaskCount} Inspections</strong>
            </p>
          ) : null}
          <p>
            A details file provides metadata mappings for images that will be uploaded to an
            inspection.
            {existingTaskCount
              ? ''
              : ' A details file is not required for single inspection upload, as the data can be manually entered once the images have been added.'}
          </p>
          {!existingTaskCount && !!(exampleCsv.length || exampleJson.length) && (
            <p>
              Click to download a sample details file in{' '}
              <StyledCSVDownloader
                data={exampleCsv}
                filename={'image_details_example'}
                type={'link'}
              >
                csv
              </StyledCSVDownloader>{' '}
              or{' '}
              <a download="image_details_example.json" href={exampleJson}>
                json
              </a>
              .
            </p>
          )}
          <p>
            Note: SkySpecs recommends uploading images via URL for file transfers greater than 4GB
            for better speed and stability.
          </p>
        </>
      )}
      {step === Steps.DetailsUpload && (
        <>
          <Upload
            accept={'.csv,.json'}
            beforeUpload={() => false} // bypass antd callbacks; handle change ourselves
            // @ts-ignore - tedious antd types; this is fine
            onChange={handleUpload}
          >
            <Button className="testid-upload-image-details" icon={<UploadOutlined />}>
              Upload Details
            </Button>
          </Upload>
          <br />
          {existingTaskCount ? (
            <>
              <p>
                If you do not have an image details file for the images from Inspections related to
                these Tasks, you can generate one to edit that is pre-populated with Task Ids using
                one of the options below.
                <br />
                <br />
                <StyledCSVDownloader
                  data={exampleCsv}
                  filename={'details'}
                  type={'link'}
                  marginRight={'0.5rem'}
                >
                  <Button icon={<DownloadOutlined />}>details.csv</Button>
                </StyledCSVDownloader>
                <Button icon={<DownloadOutlined />} download="details.json" href={exampleJson}>
                  details.json
                </Button>
              </p>
            </>
          ) : null}
          <ButtonContainer>
            <Button disabled={initialStep === Steps.DetailsUpload} onClick={handleBack}>
              Back
            </Button>
            {uploadType === UploadTypes.Single && (
              <Button onClick={() => setStep(Steps.ImageUpload)}>Skip</Button>
            )}
          </ButtonContainer>
        </>
      )}
      {step === Steps.DetailsMapping && (
        <>
          <Divider />
          <Row gutter={{ sm: 16 }}>
            <Col flex="50%">
              <h3>
                Selected file: <strong>{file?.name}</strong>
              </h3>
              <p>Select a source column for each Details attribute</p>
            </Col>
            <Col flex="50%">
              <Checkbox
                checked={shouldAutoRelease}
                onChange={() => setShouldAutoRelease(current => !current)}
              >
                Automatically release inspection{uploadType === UploadTypes.Multiple ? 's' : ''}{' '}
                after successful upload.{' '}
                <TooltipWithQuestionIcon
                  message={
                    'When this box is unchecked, inspections will remain in the Draft state until they are released by a user.'
                  }
                />
              </Checkbox>
            </Col>
          </Row>
          <FieldMappingForm
            fieldMappings={fieldMappings}
            fileType={fileType}
            ignoreRequired={!shouldAutoRelease}
            internalFields={internalFields}
            providedFieldNames={providedFieldNames ?? []}
            previousStep={Steps.DetailsUpload}
            nextStep={Steps.DetailsPreview}
            setStep={setStep}
            setFieldMappings={setFieldMappings}
            likelyDelimiterError={likelyDelimiterError}
            cellDelimiter={cellDelimiter}
            setCellDelimiter={setCellDelimiter}
            decimalDelimiter={decimalDelimiter}
            setDecimalDelimiter={setDecimalDelimiter}
          />
        </>
      )}
      {step === Steps.DetailsPreview && (
        <Preview
          uploadType={uploadType}
          inspectionIds={inspectionIds}
          fieldMappings={fieldMappings}
          previewData={previewData}
          setStep={setStep}
          handleComplete={handleValidate}
        />
      )}
      {step === Steps.DetailsValidation && validationStepProps && (
        <Validation {...validationStepProps} />
      )}
    </div>
  );

  const createPanels: { title: string; content: JSX.Element }[] = [
    {
      title: 'Image Detail Upload',
      content: defaultCreatePanel,
    },
    ...(shouldShowSkyCrawler
      ? [
          {
            title: 'SkyCrawler Upload',
            content: (
              <SkyCrawlerUpload
                inspectionId={inspectionIds[0]}
                onComplete={() => {
                  onComplete && onComplete();
                  onCloseModal();
                }}
                onBack={handleBack}
              />
            ),
          },
        ]
      : []),
    ...(shouldShowAeroEnterprise
      ? [
          {
            title: 'Aero Enterprise Upload',
            content: (
              <AeroEnterpriseUpload
                inspectionId={inspectionIds[0]}
                onComplete={() => {
                  onComplete && onComplete();
                  onCloseModal();
                }}
                onBack={handleBack}
              />
            ),
          },
        ]
      : []),
  ];

  return createPanels.length > 1 ? (
    <Collapse accordion={true} defaultActiveKey={0}>
      {createPanels.map((panel, index) => {
        const { title, content } = panel;
        return (
          <Collapse.Panel header={title} key={index}>
            {content}
          </Collapse.Panel>
        );
      })}
    </Collapse>
  ) : (
    defaultCreatePanel
  );
};
